import Paper from '@material-ui/core/Paper';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '~/store';
import {
  changeInsert,
  getDocumentRequest,
} from '~/store/ducks/document/actions';
import { cleanModal } from '~/store/ducks/ManterAtividadesTratamentoDados/actions';
import Modal from './modal';

interface Data {
  page_id: number;
  type: string;
  name: string;
  status: string;
  acao: any;
  total_itens: number;
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = 'asc' | 'desc';

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string },
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

interface HeadCell {
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
}

const headCells: HeadCell[] = [
  {
    id: 'status',
    numeric: false,
    disablePadding: false,
    label: 'Status',
  },
  {
    id: 'type',
    numeric: false,
    disablePadding: false,
    label: 'Tipo',
  },
  {
    id: 'name',
    numeric: false,
    disablePadding: false,
    label: 'Nome',
  },
  {
    id: 'acao',
    numeric: true,
    disablePadding: false,
    label: 'Ação',
  },
];

interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  numSelected: number;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data,
  ) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { classes, order, orderBy, onRequestSort } = props;
  const createSortHandler =
    (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map(headCell => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'default'}
            sortDirection={orderBy === headCell.id ? order : false}
            style={{ paddingRight: headCell.id === 'acao' ? '50px' : '' }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    paper: {
      width: '100%',
      marginBottom: theme.spacing(2),
    },
    table: {
      minWidth: 750,
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
  }),
);

interface IEnhancedTableProps extends React.HTMLAttributes<HTMLDivElement> {
  data?: Data[];
  ResetPage?: number;
  getOrderHandleChangePage?: (value: number, rowsPerPage: number) => void;
  getOrderHandleChangeRowsPerPage?: (
    value: number,
    rowsPerPage: string,
  ) => void;
  toggleAction?: boolean;
}

export const EnhancedTable: React.FC<IEnhancedTableProps> = ({
  data = [],
  getOrderHandleChangePage,
  getOrderHandleChangeRowsPerPage,
  toggleAction,
  ResetPage,
  ...resst
}) => {
  const classes = useStyles();
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('name');
  const [selected, setSelected] = React.useState<string[]>([]);
  const [page, setPage] = React.useState(0);
  const [dense, setDense] = React.useState(false);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  useEffect(() => {
    setPage(0);
  }, [ResetPage]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data,
  ) => {
    const isAsc = orderBy === property && order === 'asc';

    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = data.map(n => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    if (getOrderHandleChangePage) {
      getOrderHandleChangePage(newPage, rowsPerPage);
    }
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (getOrderHandleChangeRowsPerPage) {
      getOrderHandleChangeRowsPerPage(page, event.target.value);
    }

    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const company = useSelector<ApplicationState, any>(
    state => state.auth.company,
  );

  const [exibir, setExibir] = useState(false);
  const [dataRow, setDataRow] = useState({});

  const dispatch = useDispatch();

  const handleEditing = useCallback(
    (row: any) => {
      setDataRow(row);
    },
    [setDataRow],
  );

  return (
    <div className={`${classes.root} ${resst.className}`}>
      <Paper className={classes.paper}>
        <TableContainer>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size={dense ? 'small' : 'medium'}
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={data.length}
            />
            <TableBody>
              {stableSort(data, getComparator(order, orderBy)).map(
                (row, index) => {
                  return (
                    <TableRow hover tabIndex={-1} key={index + 1}>
                      <TableCell align="left" style={{ lineHeight: '1.6rem' }}>
                        {row.status === 'Y' ? 'Ativado' : 'Desativado'}
                      </TableCell>
                      <TableCell align="left" style={{ lineHeight: '1.6rem' }}>
                        {row.type}
                      </TableCell>
                      <TableCell align="left" style={{ lineHeight: '1.6rem' }}>
                        {row.name}
                      </TableCell>

                      <TableCell align="right">
                        <div>
                          <button
                            style={{
                              fontSize: '1.1rem',
                              marginBottom: 5,
                              background:
                                'linear-gradient(45deg,#135db4 30%,#1473e6 90%)',
                              border: 'none',
                              padding: '5px 18px',
                              borderRadius: 50,
                            }}
                            type="button"
                            data-toggle="modal"
                            data-target="#exampleModal"
                            onClick={() => {
                              dispatch(
                                getDocumentRequest(
                                  company?.country_company_id,
                                  row.page_id,
                                ),
                              );
                              setExibir(true);
                              dispatch(changeInsert('change'));
                              dispatch(cleanModal());
                              handleEditing(row);
                            }}
                          >
                            Editar
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                },
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={data[0] ? data[0].total_itens : 0}
          rowsPerPage={rowsPerPage}
          page={page}
          labelRowsPerPage=""
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>

      <Modal row={dataRow} action="change" />
    </div>
  );
};

export default EnhancedTable;
